/**
 * Tutor assignment decision modal.
 * Recruiter pre-validates (retain / refuse-all); coordinator validates or direct-assigns; admin can do both in one view.
 */
import { useState, useMemo } from 'react';
import { CheckCircle2, XCircle, Info, AlertTriangle, Building2 } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { Select } from '@/components/forms/Select';
import { Textarea } from '@/components/forms/Textarea';
import { LabelBadge } from '@/components/common/LabelBadge';
import {
  TUTOR_ASSIGNMENT_DECISION_DESCRIPTION,
  TUTOR_ASSIGNMENT_REFUSAL_IMPACT,
  TUTOR_ASSIGNMENT_REFUSAL_REASONS,
  TUTOR_ASSIGNMENT_REFUSAL_SELECT_OPTIONS,
} from '@/components/tutors/tutor-configs';
import {
  formatAssignmentRefusalReason,
  getAssignmentDecisionHeader,
  getTutorAssignmentStatusColor,
  getTutorAssignmentStatusLabel,
} from '@/components/tutors/inperson/tutor-form-utils';
import {
  useRetainEstablishments,
  useRefuseAllEstablishments,
  useValidateAssignment,
  useRefuseAssignment,
  useInPersonTutorAssignments,
  useCoordinatorDirectAssign,
} from '@/hooks/useUsers';
import { getErrorMessage } from '@/utils/errors-utils';
import { toast } from '@/components/ui/toast';
import { useEstablishmentsByIds } from '@/hooks/useEstablishments';
import {
  deriveAssignmentSectionLocks,
  isAssignmentRowLocked,
  mapInPersonAssignmentsFromApi,
  mergeAssignmentWishesWithAffectations,
} from '@/components/tutors/inperson/tutor-table-utils';

/** Onboarding wish row (`my_establishments.wishes`). */
interface WishItem {
  establishmentId: string;
  establishmentName?: string;
  rank?: number;
}

/** Assignment API row (or a wish merged with its latest affectation). */
interface AssignmentItem {
  id?: string;
  establishmentId: string;
  establishmentName?: string;
  wishRank?: number;
  status?: string;
}

/** Props for {@link AssignmentDecisionModal}. */
interface AssignmentDecisionModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: 'pre-validate' | 'validate';
  readonly userId: string;
  readonly companyId: string;
  readonly departmentLabel?: string;
  readonly onSuccess?: () => void;
  /** Wished establishments from the tutor's onboarding record (my_establishments.wishes). */
  readonly wishes?: WishItem[];
  /**
   * When true, shows both recruiter (pre-validate) and coordinator (validate) sections
   * in a single scrollable view. Used for admin who has both permissions.
   */
  readonly showBothSections?: boolean;
  /** Pass the parent's loading flag so names never flash as raw IDs. */
  readonly isLoadingNames?: boolean;
}

/** Per-row decision; `undefined` means the row is still undecided. */
type RowDecision = 'accept' | 'refuse' | undefined;

/** Per-establishment accept/refuse plus optional refusal reason. */
interface RowState {
  decision: RowDecision;
  reason: string;
  customDescription: string;
}

/** Establishment id → {@link RowState} for one recruiter or coordinator section. */
type SectionRowStates = Record<string, RowState>;

/** Blank row used when the establishment has no decision yet. */
const emptyRowState = (): RowState => ({ decision: undefined, reason: '', customDescription: '' });

/** Props for {@link SectionRows}. */
interface SectionRowsProps {
  readonly displayItems: AssignmentItem[];
  readonly rowStates: SectionRowStates;
  readonly onToggle: (id: string, decision: RowDecision) => void;
  readonly onSetRow: (id: string, patch: Partial<RowState>) => void;
  readonly lockedByValidated: boolean;
  readonly decidedRows: AssignmentItem[];
  readonly isPending: boolean;
  readonly acceptLabel: string;
  readonly refuseLabel: string;
  readonly isPrevalidate: boolean;
  readonly onApplyToAll: (decision: RowDecision) => void;
  readonly onReset: () => void;
  readonly pendingPrevalidatedIds?: ReadonlySet<string>;
}

/** Props for {@link AssignmentEstablishmentRow}. */
interface AssignmentRowProps {
  readonly assignment: AssignmentItem;
  readonly displayItems: AssignmentItem[];
  readonly rowStates: SectionRowStates;
  readonly onToggle: (id: string, decision: RowDecision) => void;
  readonly onSetRow: (id: string, patch: Partial<RowState>) => void;
  readonly lockedByValidated: boolean;
  readonly isPending: boolean;
  readonly acceptLabel: string;
  readonly refuseLabel: string;
  readonly isPrevalidate: boolean;
  readonly pendingPrevalidatedIds?: ReadonlySet<string>;
}

/** Border / background by locked / accepted / refused / already-validated. */
function getAssignmentRowClassName(
  isLocked: boolean,
  isAccepted: boolean,
  isRefused: boolean,
  isValidated: boolean,
): string {
  if (isLocked) return 'opacity-40 border-border';
  if (isAccepted) {
    return 'border-[color-mix(in_srgb,rgb(var(--status-success))_50%,transparent)] bg-[color-mix(in_srgb,rgb(var(--status-success))_5%,transparent)]';
  }
  if (isRefused) {
    return 'border-[color-mix(in_srgb,rgb(var(--status-danger))_50%,transparent)] bg-[color-mix(in_srgb,rgb(var(--status-danger))_5%,transparent)]';
  }
  if (isValidated) {
    return 'border-[color-mix(in_srgb,rgb(var(--status-success))_30%,transparent)] bg-[color-mix(in_srgb,rgb(var(--status-success))_3%,transparent)]';
  }
  return 'border-border';
}

/** One establishment: accept / refuse plus optional refusal reason. */
function AssignmentEstablishmentRow({
  assignment,
  displayItems,
  rowStates,
  onToggle,
  onSetRow,
  lockedByValidated,
  isPending,
  acceptLabel,
  refuseLabel,
  isPrevalidate,
  pendingPrevalidatedIds,
}: AssignmentRowProps) {
  const row = rowStates[assignment.establishmentId] ?? emptyRowState();
  const isAccepted = row.decision === 'accept';
  const isRefused = row.decision === 'refuse';
  const { isValidated, isPreValidated, isLocked } = isAssignmentRowLocked(
    assignment,
    displayItems,
    rowStates,
    lockedByValidated,
    isPrevalidate,
    pendingPrevalidatedIds,
  );
  const reasonConfig = TUTOR_ASSIGNMENT_REFUSAL_REASONS.find((r) => r.code === row.reason);

  return (
    <div
      className={`rounded-lg border transition-colors ${getAssignmentRowClassName(isLocked, isAccepted, isRefused, isValidated)}`}
    >
      <div className="flex items-center gap-3 p-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium">
              {assignment.establishmentName || assignment.establishmentId}
            </span>
            {!!assignment.wishRank && (
              <span className="text-xs text-muted-foreground shrink-0">
                Vœu #{assignment.wishRank}
              </span>
            )}
          </div>
          {assignment.status && (
            <div className="mt-1 ml-5">
              <LabelBadge
                label={getTutorAssignmentStatusLabel(assignment.status)}
                variant="outlined"
                color={getTutorAssignmentStatusColor(assignment.status)}
                size="sm"
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="outline"
            size="sm"
            icon={CheckCircle2}
            color={isAccepted ? undefined : 'var(--color-3-nuance-1)'}
            className={
              isAccepted
                ? 'bg-[color-mix(in_srgb,rgb(var(--status-success))_15%,transparent)] border-[color-mix(in_srgb,rgb(var(--status-success))_60%,transparent)] text-[rgb(var(--status-success))] hover:bg-[color-mix(in_srgb,rgb(var(--status-success))_25%,transparent)]'
                : ''
            }
            onClick={() => onToggle(assignment.establishmentId, 'accept')}
            disabled={isPending || isLocked || isValidated || isPreValidated}
          >
            {acceptLabel}
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={XCircle}
            color={isRefused ? undefined : 'rgb(var(--status-danger))'}
            className={
              isRefused
                ? 'bg-[color-mix(in_srgb,rgb(var(--status-danger))_15%,transparent)] border-[color-mix(in_srgb,rgb(var(--status-danger))_60%,transparent)] text-[rgb(var(--status-danger))] hover:bg-[color-mix(in_srgb,rgb(var(--status-danger))_25%,transparent)]'
                : ''
            }
            onClick={() => onToggle(assignment.establishmentId, 'refuse')}
            disabled={isPending || isLocked || isPreValidated}
          >
            {refuseLabel}
          </Button>
        </div>
      </div>

      {isRefused && (
        <div className="px-3 pb-3 space-y-2 border-t border-[color-mix(in_srgb,rgb(var(--status-danger))_20%,transparent)]">
          <p className="text-xs text-muted-foreground pt-2">Motif de refus</p>
          <Select
            label=""
            required
            size="sm"
            placeholder="Sélectionnez un motif"
            value={row.reason}
            onChange={(value) => {
              onSetRow(assignment.establishmentId, {
                reason: value,
                customDescription: value !== 'other' ? '' : row.customDescription,
              });
            }}
            disabled={isPending}
            options={TUTOR_ASSIGNMENT_REFUSAL_SELECT_OPTIONS}
          />
          {reasonConfig?.requiresDescription && (
            <Textarea
              label="Précisez le motif"
              required
              placeholder="Détaillez la raison du refus..."
              value={row.customDescription}
              onChange={(e) =>
                onSetRow(assignment.establishmentId, {
                  customDescription: e.target.value,
                })
              }
              rows={2}
              disabled={isPending}
            />
          )}
          <div
            className="rounded-lg border p-2.5 text-xs"
            style={{
              borderColor: 'color-mix(in srgb, rgb(var(--status-danger)) 30%, transparent)',
              background: 'color-mix(in srgb, rgb(var(--status-danger)) 6%, transparent)',
            }}
          >
            <p
              className="font-medium flex items-center gap-1"
              style={{ color: 'var(--color-1-nuance-1)' }}
            >
              <AlertTriangle
                className="w-3.5 h-3.5 shrink-0"
                style={{ color: 'rgb(var(--status-danger))' }}
              />
              Impact
            </p>
            <p className="mt-1 text-muted-foreground">
              {isPrevalidate
                ? TUTOR_ASSIGNMENT_REFUSAL_IMPACT.prevalidate
                : TUTOR_ASSIGNMENT_REFUSAL_IMPACT.validate}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/** Accept/refuse list plus apply-to-all / reset actions. */
function SectionRows({
  displayItems,
  rowStates,
  onToggle,
  onSetRow,
  lockedByValidated,
  decidedRows,
  isPending,
  acceptLabel,
  refuseLabel,
  isPrevalidate,
  onApplyToAll,
  onReset,
  pendingPrevalidatedIds,
}: SectionRowsProps) {
  if (displayItems.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-4">
        Aucun établissement à gérer
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {displayItems.map((assignment) => (
        <AssignmentEstablishmentRow
          key={assignment.establishmentId}
          assignment={assignment}
          displayItems={displayItems}
          rowStates={rowStates}
          onToggle={onToggle}
          onSetRow={onSetRow}
          lockedByValidated={lockedByValidated}
          isPending={isPending}
          acceptLabel={acceptLabel}
          refuseLabel={refuseLabel}
          isPrevalidate={isPrevalidate}
          pendingPrevalidatedIds={pendingPrevalidatedIds}
        />
      ))}

      {!lockedByValidated && displayItems.length > 1 && (
        <div className="flex items-center gap-2 pt-2 border-t">
          {isPrevalidate && (
            <Button
              variant="outline"
              size="sm"
              icon={CheckCircle2}
              color="var(--color-3-nuance-1)"
              onClick={() => onApplyToAll('accept')}
              disabled={isPending}
            >
              Tout {acceptLabel.toLowerCase()}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            icon={XCircle}
            color="rgb(var(--status-danger))"
            onClick={() => onApplyToAll('refuse')}
            disabled={isPending}
          >
            Tout {refuseLabel.toLowerCase()}
          </Button>
          {decidedRows.length > 0 && (
            <Button variant="ghost" size="sm" onClick={onReset} disabled={isPending}>
              Réinitialiser
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Local accept/refuse state for one section (pre-validate or validate). */
function useAssignmentSection(interactiveItems: AssignmentItem[]) {
  const [rowStates, setRowStates] = useState<SectionRowStates>({});
  /** Current row, or a blank {@link emptyRowState} if untouched. */
  const getRow = (id: string): RowState => rowStates[id] ?? emptyRowState();
  /** Merge a patch into one establishment's row (fills missing keys from blank). */
  const setRow = (id: string, patch: Partial<RowState>) => {
    setRowStates((prev) => ({
      ...prev,
      [id]: { ...emptyRowState(), ...prev[id], ...patch },
    }));
  };
  /** Clicking the same decision again clears it. */
  const toggleDecision = (id: string, decision: RowDecision) => {
    const current = getRow(id).decision;
    setRow(id, { decision: current === decision ? undefined : decision });
  };
  /** Apply the same decision to every interactive establishment. */
  const applyToAll = (decision: RowDecision) => {
    setRowStates((prev) => {
      const next: SectionRowStates = {};
      interactiveItems.forEach((item) => {
        next[item.establishmentId] = {
          ...emptyRowState(),
          ...prev[item.establishmentId],
          decision,
        };
      });
      return next;
    });
  };
  return {
    rowStates,
    setRowStates,
    getRow,
    setRow,
    toggleDecision,
    applyToAll,
    decidedRows: interactiveItems.filter(
      (item) => getRow(item.establishmentId).decision !== undefined,
    ),
    refusedRows: interactiveItems.filter(
      (item) => getRow(item.establishmentId).decision === 'refuse',
    ),
    acceptedRows: interactiveItems.filter(
      (item) => getRow(item.establishmentId).decision === 'accept',
    ),
  };
}

/** Read-only slice of section state used by submit helpers. */
type AssignmentSectionSnapshot = {
  acceptedRows: AssignmentItem[];
  refusedRows: AssignmentItem[];
  getRow: (id: string) => RowState;
};

/** Mutation fns passed into submit so the helpers stay UI-free. */
type AssignmentSubmitMutations = {
  retain: (dto: {
    userId: string;
    companyId: string;
    establishmentIds: string[];
  }) => Promise<unknown>;
  refuseAll: (dto: {
    userId: string;
    companyId: string;
    reason: string;
    establishmentIds: string[];
  }) => Promise<unknown>;
  validate: (dto: { userId: string; companyId: string; affectationId: string }) => Promise<unknown>;
  directAssign: (dto: {
    userId: string;
    companyId: string;
    establishmentId: string;
  }) => Promise<unknown>;
  refuse: (dto: {
    userId: string;
    companyId: string;
    affectationId: string;
    reason: string;
  }) => Promise<unknown>;
};

/** Retain accepted wishes; refuse-all each refused establishment. */
async function submitPrevalidateDecisions(
  userId: string,
  companyId: string,
  section: AssignmentSectionSnapshot,
  mutations: Pick<AssignmentSubmitMutations, 'retain' | 'refuseAll'>,
) {
  if (section.acceptedRows.length > 0) {
    await mutations.retain({
      userId,
      companyId,
      establishmentIds: section.acceptedRows.map((item) => item.establishmentId),
    });
  }
  await Promise.all(
    section.refusedRows.map((item) => {
      const row = section.getRow(item.establishmentId);
      return mutations.refuseAll({
        userId,
        companyId,
        reason: formatAssignmentRefusalReason(row.reason, row.customDescription),
        establishmentIds: [item.establishmentId],
      });
    }),
  );
}

/** Validate existing affectation or direct-assign a wish that has no row yet. */
async function submitValidateDecisions(
  userId: string,
  companyId: string,
  section: AssignmentSectionSnapshot,
  mutations: Pick<AssignmentSubmitMutations, 'validate' | 'directAssign' | 'refuse'>,
) {
  await Promise.all(
    section.acceptedRows.map((item) =>
      item.id
        ? mutations.validate({ userId, companyId, affectationId: item.id })
        : mutations.directAssign({ userId, companyId, establishmentId: item.establishmentId }),
    ),
  );
  await Promise.all(
    section.refusedRows
      .filter((item): item is typeof item & { id: string } => Boolean(item.id))
      .map((item) => {
        const row = section.getRow(item.establishmentId);
        return mutations.refuse({
          userId,
          companyId,
          affectationId: item.id,
          reason: formatAssignmentRefusalReason(row.reason, row.customDescription),
        });
      }),
  );
}

/** Run pre-validate and/or validate depending on modal mode (`showBothSections` = admin). */
async function submitAssignmentDecisions(params: {
  userId: string;
  companyId: string;
  showBothSections: boolean;
  isPrevalidate: boolean;
  pre: AssignmentSectionSnapshot;
  val: AssignmentSectionSnapshot;
  single: AssignmentSectionSnapshot;
  mutations: AssignmentSubmitMutations;
}) {
  const preSection = params.showBothSections ? params.pre : params.single;
  const valSection = params.showBothSections ? params.val : params.single;
  if (params.showBothSections || params.isPrevalidate) {
    await submitPrevalidateDecisions(params.userId, params.companyId, preSection, params.mutations);
  }
  if (params.showBothSections || !params.isPrevalidate) {
    await submitValidateDecisions(params.userId, params.companyId, valSection, params.mutations);
  }
}

/** Recruiter / coordinator section title + description. */
function SectionBanner({ title, description }: Readonly<{ title: string; description: string }>) {
  return (
    <div className="space-y-3">
      <div
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold"
        style={{
          background: 'color-mix(in srgb, rgb(var(--color-2-nuance-1)) 10%, transparent)',
          color: 'rgb(var(--color-2-nuance-1))',
        }}
      >
        <Info className="w-3.5 h-3.5 shrink-0" />
        {title}
      </div>
      <p className="text-xs text-muted-foreground px-1">{description}</p>
    </div>
  );
}

/** Placeholder rows while establishment names resolve (avoids flashing raw ids). */
function NamesLoadingSkeleton({ count }: Readonly<{ count: number }>) {
  return (
    <div className="space-y-3">
      {[0, 1, 2].slice(0, count).map((i) => (
        <div
          key={i}
          className="rounded-lg border border-border p-3 flex items-center gap-3 animate-pulse"
        >
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-2/3 rounded bg-muted" />
            <div className="h-3 w-1/3 rounded bg-muted" />
          </div>
          <div className="flex gap-1">
            <div className="h-8 w-20 rounded bg-muted" />
            <div className="h-8 w-20 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Tutor assignment decision modal. */
export function AssignmentDecisionModal({
  open,
  onClose,
  mode,
  userId,
  companyId,
  departmentLabel,
  onSuccess,
  wishes,
  showBothSections = false,
  isLoadingNames: isLoadingNamesFromParent = false,
}: AssignmentDecisionModalProps) {
  const { data: assignmentsData, isLoading: isLoadingAssignments } = useInPersonTutorAssignments(
    userId,
    companyId,
  );

  /** Wish ids missing a name — fetched so the list never flashes raw UUIDs. */
  const wishIdsToResolve = useMemo(
    () =>
      (wishes ?? [])
        .filter((w) => !!w.establishmentId && !w.establishmentName)
        .map((w) => w.establishmentId),
    [wishes],
  );
  const { establishments: resolvedEstabs, isLoading: isLoadingResolved } =
    useEstablishmentsByIds(wishIdsToResolve);

  /** True until assignment rows and missing establishment names are ready. */
  const isResolvingNames = isLoadingNamesFromParent || isLoadingResolved || isLoadingAssignments;

  /** Latest assignment rows from the API, shaped for the list. */
  const assignments: AssignmentItem[] = useMemo(
    () => mapInPersonAssignmentsFromApi(assignmentsData),
    [assignmentsData],
  );

  /** Merge wishes with latest assignment per establishment (API is DESC, so reverse first). */
  const displayItems: AssignmentItem[] = useMemo(
    () => mergeAssignmentWishesWithAffectations(wishes, assignments, resolvedEstabs),
    [wishes, assignments, resolvedEstabs],
  );

  const retainMutation = useRetainEstablishments();
  const refuseAllMutation = useRefuseAllEstablishments();
  const validateMutation = useValidateAssignment();
  const refuseMutation = useRefuseAssignment();
  const directAssignMutation = useCoordinatorDirectAssign();

  /** Any assignment mutation in flight — lock the whole form. */
  const isPending =
    retainMutation.isPending ||
    refuseAllMutation.isPending ||
    validateMutation.isPending ||
    refuseMutation.isPending ||
    directAssignMutation.isPending;

  /** Recruiter mode (retain / refuse-all) vs coordinator mode (validate / refuse). */
  const isPrevalidate = mode === 'pre-validate';
  const {
    lockedByValidated,
    interactiveItems,
    preLockedByValidated,
    preInteractiveItems,
    valLockedByValidated,
    valInteractiveItems,
  } = deriveAssignmentSectionLocks(displayItems, isPrevalidate);
  /** Single-section state when the modal is not in admin (both-sections) mode. */
  const single = useAssignmentSection(interactiveItems);
  /** Recruiter (pre-validate) section state used when `showBothSections` is on. */
  const pre = useAssignmentSection(preInteractiveItems);
  /** Coordinator (validate) section state used when `showBothSections` is on. */
  const val = useAssignmentSection(valInteractiveItems);

  /** Every refused row must have a reason (and a description when the reason requires one). */
  const isRefuseValid = (rows: AssignmentItem[], getRowFn: (id: string) => RowState): boolean =>
    rows.every((item) => {
      const row = getRowFn(item.establishmentId);
      const config = TUTOR_ASSIGNMENT_REFUSAL_REASONS.find((r) => r.code === row.reason);
      return row.reason && (!config?.requiresDescription || row.customDescription.trim());
    });

  /** At least one decision, and every refusal is complete. */
  const canSubmit = showBothSections
    ? (pre.decidedRows.length > 0 || val.decidedRows.length > 0) &&
      isRefuseValid(pre.refusedRows, pre.getRow) &&
      isRefuseValid(val.refusedRows, val.getRow)
    : single.decidedRows.length > 0 && isRefuseValid(single.refusedRows, single.getRow);

  /** Close and wipe local decisions so the next open starts clean. */
  const handleClose = () => {
    onClose();
    single.setRowStates({});
    pre.setRowStates({});
    val.setRowStates({});
  };

  /** Submit pre-validate and/or validate decisions, then close. */
  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await submitAssignmentDecisions({
        userId,
        companyId,
        showBothSections,
        isPrevalidate,
        pre,
        val,
        single,
        mutations: {
          retain: retainMutation.mutateAsync,
          refuseAll: refuseAllMutation.mutateAsync,
          validate: validateMutation.mutateAsync,
          directAssign: directAssignMutation.mutateAsync,
          refuse: refuseMutation.mutateAsync,
        },
      });
      handleClose();
      onSuccess?.();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    }
  };

  /** Icon / title / colors for recruiter, coordinator, or admin (both) mode. */
  const header = getAssignmentDecisionHeader(showBothSections, isPrevalidate);

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      size="xl"
      header={{
        icon: header.icon,
        iconSize: 'md',
        iconColor: header.iconColor,
        iconBgColor: header.iconBgColor,
        title: header.title,
        badge: departmentLabel ? (
          <LabelBadge
            label={departmentLabel}
            variant="outlined"
            size="sm"
            color="var(--muted-foreground)"
          />
        ) : undefined,
        description: TUTOR_ASSIGNMENT_DECISION_DESCRIPTION,
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      footer={
        <div className="flex gap-2 justify-end w-full">
          <Button preset="modal-cancel" onClick={handleClose} disabled={isPending}>
            Annuler
          </Button>
          <Button
            preset="modal-confirm"
            onClick={handleSubmit}
            loading={isPending}
            disabled={!canSubmit || isPending || isResolvingNames}
          >
            Confirmer
          </Button>
        </div>
      }
    >
      {isResolvingNames && <NamesLoadingSkeleton count={wishes?.length || 2} />}
      {!isResolvingNames && showBothSections && (
        <div className="space-y-6">
          <div className="space-y-3">
            <SectionBanner
              title="Recruteur — Prévalidation"
              description="Le recruteur présélectionne un ou plusieurs établissements parmi les choix du tuteur avant validation par le coordinateur."
            />
            <SectionRows
              displayItems={displayItems}
              rowStates={pre.rowStates}
              onToggle={pre.toggleDecision}
              onSetRow={pre.setRow}
              lockedByValidated={preLockedByValidated}
              decidedRows={pre.decidedRows}
              isPending={isPending}
              acceptLabel="Prévalider"
              refuseLabel="Prérefuser"
              isPrevalidate={true}
              onApplyToAll={pre.applyToAll}
              onReset={() => pre.setRowStates({})}
            />
          </div>
          <div className="border-t border-dashed border-border" />
          <div className="space-y-3">
            <SectionBanner
              title="Coordinateur — Validation"
              description="Le coordinateur valide un seul établissement parmi ceux prévalidés. Cet établissement devient l'affectation définitive du tuteur."
            />
            <SectionRows
              displayItems={displayItems}
              rowStates={val.rowStates}
              onToggle={val.toggleDecision}
              onSetRow={val.setRow}
              lockedByValidated={valLockedByValidated}
              decidedRows={val.decidedRows}
              isPending={isPending}
              acceptLabel="Valider"
              refuseLabel="Refuser"
              isPrevalidate={false}
              onApplyToAll={val.applyToAll}
              onReset={() => val.setRowStates({})}
              pendingPrevalidatedIds={new Set(pre.acceptedRows.map((item) => item.establishmentId))}
            />
          </div>
        </div>
      )}
      {!isResolvingNames && !showBothSections && (
        <div className="space-y-3">
          {displayItems.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-6">
              Aucun établissement à gérer
            </div>
          )}
          {displayItems.length > 0 && (
            <SectionRows
              displayItems={displayItems}
              rowStates={single.rowStates}
              onToggle={single.toggleDecision}
              onSetRow={single.setRow}
              lockedByValidated={lockedByValidated}
              decidedRows={single.decidedRows}
              isPending={isPending}
              acceptLabel={isPrevalidate ? 'Prévalider' : 'Valider'}
              refuseLabel={isPrevalidate ? 'Prérefuser' : 'Refuser'}
              isPrevalidate={isPrevalidate}
              onApplyToAll={single.applyToAll}
              onReset={() => single.setRowStates({})}
            />
          )}
        </div>
      )}
    </BaseModal>
  );
}
